import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from '../../api/axios';
import FlowCanvas from './FlowCanvas';
import FlowNodeRenderer from './FlowNodeRenderer';
import LoadingSpinner from '../common/LoadingSpinner';
import './FeatureFlow.scss';

const FeatureFlowPage = ({ 
  embedded = false, 
  campaignId: campaignIdProp,
  featureInstanceId: featureInstanceIdProp,
  liveCustomisationData,
  liveFeatureData,
  featureVarKey: featureVarKeyProp, 
  streamId: streamIdProp,
  onClose,
  onResetView,
  onActivateNode,
  onRemoveGraphic,
  onPollQuestionChange,
  onPollAnswerChange,
  onPollAnswerAdd,
  onPollAnswerRemove,
  onImageFieldChange,
  onGoalFieldChange,
  onPresetFieldChange,
  onFlowBoolChange,
  onFlowFieldChange,
  getStepItems,
  onPollTriggerDrop,
  onPollTriggerRemove,
  // Optional callbacks for generic disruptor-trigger flow nodes
  onActivateDisruptor,
  onDropDisruptor,
  onRemoveDisruptor,
  onGoalRewardDisruptorDrop,
  // Optional campaign features list so FlowCanvas can resolve IDs to labels
  campaignFeatures,
  onClearSelection,
  // Active audio slot varName — highlights the matching row in PollAudioNode
  activeAudioSlotVarName,
  onAddPreset,
  onVideoSettingsChange,
  onSlotOptionDisruptorDrop,
  onSlotOptionDisruptorRemove,
  onSlotOptionRemove,
  onSlotResultAdd,
  onSlotResultRemove,
  onSlotResultDisruptorDrop,
  onSlotResultDisruptorRemove,
  activeNode,
  selectedFieldDataLink,
  onTriviaQuestionDisruptorDrop,
  onSelectPlacedComponent,
}) => {
  // Always call hooks at top level (React rules)
  const params = useParams();
  const [searchParams] = useSearchParams();
  
  // When embedded, use props from CampaignContext. Otherwise use router values
  const campaignID = embedded ? campaignIdProp : params.campaignID;
  const featureVarKey = embedded ? featureVarKeyProp : params.featureVarKey;
  const featureInstanceId = embedded ? featureInstanceIdProp : searchParams?.get('featureInstanceId');
  const streamID = embedded ? streamIdProp : searchParams?.get('streamID');

  const [flowData, setFlowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [zoom, setZoom] = useState(1);
  const lastRefreshedFeatureSignatureRef = useRef(null);
  const campaignFeatureSignature = Array.isArray(campaignFeatures)
    ? campaignFeatures.map((feature) => String(feature?._id || '')).join(',')
    : '';

  useEffect(() => {
    if (campaignID && featureVarKey) {
      loadFlowData();
    }
  }, [campaignID, featureInstanceId, featureVarKey, streamID]);

  // silent=true skips the loading spinner so the canvas stays in place (used after hashtag save).
  const loadFlowData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const cacheBust = Date.now();
      const qs = new URLSearchParams();
      if (streamID) qs.set('streamID', streamID);
      if (featureInstanceId) qs.set('featureInstanceId', featureInstanceId);
      qs.set('_ts', String(cacheBust));
      const url = `/campaigns/flow/${campaignID}/${featureVarKey}?${qs.toString()}`;
      console.log('[FeatureFlow] loadFlowData', {
        embedded,
        campaignID,
        featureInstanceId,
        featureVarKey,
        streamID,
        cacheBust,
        url
      });

      const response = await axios.get(url, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });
      
      if (response.data.success) {
        setFlowData(response.data);
        setError(null);
      } else {
        if (!silent) setError(response.data.error || 'Failed to load flow data');
      }
    } catch (err) {
      console.error('[FeatureFlow] Error loading flow', {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
        url: err?.config?.url
      });
      if (!silent) setError(err.response?.data?.error || err.message || 'Failed to load flow data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Use silent refresh when called from FlowCanvas after saving —
  // avoids unmounting/remounting the canvas (no pan reset, no spinner).
  const silentRefreshFlow = () => loadFlowData(true);

  useEffect(() => {
    if (!embedded || !campaignFeatureSignature) {
      return;
    }

    if (lastRefreshedFeatureSignatureRef.current === null) {
      lastRefreshedFeatureSignatureRef.current = campaignFeatureSignature;
      return;
    }

    if (lastRefreshedFeatureSignatureRef.current === campaignFeatureSignature) {
      return;
    }

    lastRefreshedFeatureSignatureRef.current = campaignFeatureSignature;
    silentRefreshFlow();
  }, [embedded, campaignFeatureSignature]);

  const handleStepClick = (step) => {
    setSelectedStep(step);
  };

  const handleCloseDetail = () => {
    setSelectedStep(null);
  };

  if (loading) {
    return (
      <div className="feature-flow-page loading">
        <LoadingSpinner />
        <p>Loading feature flow...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feature-flow-page error">
        <div className="error-message">
          <h2>Error Loading Flow</h2>
          <p>{error}</p>
          <button onClick={loadFlowData}>Retry</button>
        </div>
      </div>
    );
  }

  if (!flowData) {
    return (
      <div className="feature-flow-page empty">
        <p>No flow data available</p>
      </div>
    );
  }

  return (
    <div className="feature-flow-page">
      <div className="flow-header">
        <div className="flow-info">
          {embedded && onClose && (
            <button className="btn-back" onClick={onClose}>
              ← Back to Timeline
            </button>
          )}
          <h1>{flowData.featureInfo.featureName} Flow</h1>
          <p className="flow-description">{flowData.featureInfo.featureDescription}</p>
        </div>
        <div className="flow-actions">
          <div className="zoom-control">
            <button className="zoom-out" onClick={() => setZoom((current) => Math.max(0.5, parseFloat((current - 0.1).toFixed(2))))}>-</button>
            <span className="zoom-level">{Math.round((zoom ?? 1) * 100)}%</span>
            <button className="zoom-in" onClick={() => setZoom((current) => Math.min(2, parseFloat((current + 0.1).toFixed(2))))}>+</button>
          </div>
        </div>
      </div>

      <div className="flow-content">
        <FlowCanvas 
          flow={flowData.flow} 
          zoom={zoom}
          onZoomChange={setZoom}
          onStepClick={handleStepClick}
          selectedStepId={selectedStep?.stepId}
          campaignID={campaignID}
          featureInfo={flowData.featureInfo}
          onRefresh={silentRefreshFlow}
          onActivateNode={onActivateNode}
          onRemoveGraphic={onRemoveGraphic}
          onPollQuestionChange={onPollQuestionChange}
          onPollAnswerChange={onPollAnswerChange}
          onPollAnswerAdd={onPollAnswerAdd}
          onPollAnswerRemove={onPollAnswerRemove}
          onImageFieldChange={onImageFieldChange}
          onGoalFieldChange={onGoalFieldChange}
          onPresetFieldChange={onPresetFieldChange}
          onFlowBoolChange={onFlowBoolChange}
          onFlowFieldChange={onFlowFieldChange}
          getStepItems={getStepItems}
          onPollTriggerDrop={onPollTriggerDrop}
          onPollTriggerRemove={onPollTriggerRemove}
          liveCustomisationData={onActivateNode ? liveCustomisationData : null}
          liveFeatureData={onActivateNode ? liveFeatureData : null}
          onActivateDisruptor={onActivateDisruptor}
          onDropDisruptor={onDropDisruptor}
          onRemoveDisruptor={onRemoveDisruptor}
          onGoalRewardDisruptorDrop={onGoalRewardDisruptorDrop}
          campaignFeatures={campaignFeatures}
          onClearSelection={onClearSelection}
          activeAudioSlotVarName={activeAudioSlotVarName}
          onAddPreset={onAddPreset}
          onVideoSettingsChange={onVideoSettingsChange}
          onSlotOptionDisruptorDrop={onSlotOptionDisruptorDrop}
          onSlotOptionDisruptorRemove={onSlotOptionDisruptorRemove}
          onSlotOptionRemove={onSlotOptionRemove}
          onSlotResultAdd={onSlotResultAdd}
          onSlotResultRemove={onSlotResultRemove}
          onSlotResultDisruptorDrop={onSlotResultDisruptorDrop}
          onSlotResultDisruptorRemove={onSlotResultDisruptorRemove}
          activeNode={activeNode}
          selectedFieldDataLink={selectedFieldDataLink}
          onTriviaQuestionDisruptorDrop={onTriviaQuestionDisruptorDrop}
          onSelectPlacedComponent={onSelectPlacedComponent}
          onResetView={onResetView}
        />
      </div>
    </div>
  );
};

export default FeatureFlowPage;
